import { fi as defaultDictionary } from "@salaxy/i18n";

import { Configs } from "./Configs";
import { Objects } from "../util/Objects";
import { DictionaryLang } from "./model";

/** Dictionary with all the known languages. */
export interface TranslationsDictionary {
  /** Finnish: The primary and source language that comes as part of the @salaxy/core library. */
  fi: DictionaryLang,
  /** English */
  en: DictionaryLang,
  /** Swedish */
  sv: DictionaryLang,
}

/**
 * Provides universal i18n methods: This is an instance class that can be instanciated for editing etc.
 * Use Translations static class to access the singleton instance attached to global / window:
 * This is woulc be the most common usecase.
 */
export class TranslationsInstance {

  /** The path to the folder that contains the locales files (i18n). */
  public localesPath = "locales";

  /*
   * Sets the function which is responsible for loading a dictionary for the given language.
   * This is an alternative to the default locales path functionality.
  */
  public localesFunc: (lang: string) => Promise<DictionaryLang>;

  /** Current language. */
  private lang: string;

  /** Assign the dictionary to static property that may be updated by the environment. */
  private dictionary : TranslationsDictionary;


  /** Tracking for languages that are already loaded to prevent unnecessary reloads. */
  private loadedLanguages: string[] = [];

  /**
   * Creates a new TranslationsInstance
   * @param dict Optional dictionary object that contains the translations.
   * The default Finnish dictionary is added in any case (for fallback) unless the Config has the
   * noCache attribute set (used in dev-mode to get the templates and i18n directly from files instead of dist).
   */
  constructor(dict = null) {
    this.dictionary = dict || {} as TranslationsDictionary;
    this.dictionary.fi = this.dictionary.fi || defaultDictionary;
    this.dictionary.sv = this.dictionary.sv || {};
    this.dictionary.en = this.dictionary.en || {};
    if (this.dictionary.fi.SALAXY) {
      this.loadedLanguages.push("fi");
      this.lang = "fi";
    }
    if (this.dictionary.sv.SALAXY) {
      this.loadedLanguages.push("sv");
    }
    if (this.dictionary.en.SALAXY) {
      this.loadedLanguages.push("en");
    }
  }

  /**
   * Translates given key to the current language.
   * @param key - Key for the text.
   * @param interpolateParams - Variables for the interpolation.
   * @param lang Language to fetch. Default is the current language.
   * @param templateOnly If true, will fetch the template directly without the interpolation.
   * @returns The translated value in current/given language, potentially interpolated.
   * If none is found...
   *
   * - If the key does not start with "SALAXY." (plain text, not a key), returns the text as-is.
   * - If the Finnish translation is found, returns it. Prefixed with red exclamation mark in test only.
   * - If fi translation not found, returns the key - always prefixed with red exclamation mark (prod and test).
   */
  public get(key: string, interpolateParams?: any, lang?: string, templateOnly = false): string {
    let template = this.getWithDefault(key, undefined, lang);

    if (!template && key?.startsWith("SALAXY.")) {
      // Fallback to Finnish, Add exclamation mark in test
      if (lang != "fi") {
        template = this.getWithDefault(key, undefined, "fi");
      }
      if (!template) {
        const keyPath = key.split(".");
        let text = keyPath[keyPath.length - 1];
        if ((text == "label" || text == "description" || text == "title") && keyPath.length > 2) {
          text = keyPath[keyPath.length - 2];
        }
        return "\u2757" + text;
      }
      if (Configs.current?.isTestData) {
        template = "\u2757" + template;
      }
    }
    if (!template) {
      return key;
    }

    if (templateOnly) {
      return template;
    }

    // interpolate
    const regex = /\{\{\s*(\S+)\s*\}\}/g;

    // array references not supported yet.
    return template.replace(regex, (_unused, varName) => {
      return this.readValue(interpolateParams, varName, "");
    });
  }

  /**
   * Translates given key to the current language with default value.
   * @param key - Key for the text.
   * @param defaultValue - The default value that is returned if the key is not found. Null is the default.
   * @param lang Language to fetch. Default is the current language.
   * @returns The translated value in current language or if none is found the key.
   */
  public getWithDefault(key: string, defaultValue = "", lang?: string | null): string {
    if (!key) {
      return defaultValue;
    }
    const value = this.getAny(key, lang);
    if (value && typeof value === "string") {
      return value;
    }
    return defaultValue;
  }

  /**
   * Gets any object (string or dictionary) with the given property path.
   * @param key Key for the text.
   * @param lang Language to fetch. Default is the current language.
   * @returns The object if found using the key, null if none is found.
   */
  public getAny(key: string, lang?: string | null): any {
    if (!key) {
      return null;
    }
    const subKeys = key.split(".");
    let value = this.getCurrentDictionary()[lang || this.lang];
    for (const subKey of subKeys) {
      if (Objects.has(value, subKey)) {
        value = value[subKey];
      } else {
        return null;
      }
    }
    return value;
  }

  /**
   * Loads a language bundle from the server on-demand.
   * The method will check the loadedLanguages collection, so it will load a language only once => Can be called every time the user language changes.
   * This method is designed to fail outside browser. Browser is recognized based on having globalThis.fetch.
   * The default Finnish dictionary is added added from code lib (not http) unless the Config has the
   * noCache attribute set (used in dev-mode to get the templates and i18n directly from files instead of dist).
   * @param lang Language to load.
   */
  public async loadLanguage(lang: string): Promise<void> {
    if (!this.isBrowser()) {
      const msg = "Language resources can be loaded on-demand only in browser environments!";
      console.error(msg);
      return Promise.reject(msg);
    }

    if (!this.dictionary?.[lang]) {
      const msg = `Cannot load the requested language (${lang}), the language is not supported!`;
      console.error(msg);
      return Promise.reject(msg);
    }

    if (this.loadedLanguages.includes(lang)) {
      return Promise.resolve();
    }

    if (lang == "fi" && !(Configs.current as any)?.noCache) {
      this.loadedLanguages.push(lang);
      this.addDictionary(lang, defaultDictionary);
      return Promise.resolve();
    }

    const result = await this.fetchLanguage(lang);
    this.loadedLanguages.push(lang);
    this.addDictionary(lang, result);
  }

  /**
   * Adds a dictionary. Note that adding is really merge: non-existent keys will not be removed.
   * @param lang - ISO language code for the dictionary to be added: fi, en or sv.
   * @param translations - A translations object (json).
   */
  public addDictionary(lang: string, translations: DictionaryLang): void {
    const newDictionary = {};
    newDictionary[lang] = translations;
    this.dictionary = this.mergeDeep(this.dictionary, newDictionary);
  }

  /**
   * Add all dictionaries from a single language object.
   * Note that adding is really merge: non-existent keys will not be removed.
   * @param translations - A translations object (json).
   * This object must have language codes as first level keys.
   */
  public addDictionaries(translations: {[key: string]: DictionaryLang }): void {
    for (const key in translations) {
      if (Objects.has(translations, key)) {
        this.addDictionary(key, translations[key]);
      }
    }
  }

  /**
   * Sets a collection of terms (potentially different languages) that can be used in
   * ad-hoc changes of terms or when loading customized texts in service model.
   * @param terms An object with terms in format { [key: string]: string } where key
   * is a full key starting from the language, e.g. "fi.SALAXY.NG1_Sitemap.home"
   * and value is always an indidivual text (never an object).
   */
  public setTerms(terms: { [key: string]: string }): void {
    if (!terms) {
      return;
    }
    return Object.keys(terms).forEach((key) => {
      Objects.setProperty(this.dictionary, key, terms[key]);
    });
  }

  /**
   * Sets the Salaxy user interface language.
   * @param lang - One of the supported languages: "fi", "en" or "sv".
   */
  public setLanguage(lang: "fi" | "en" | "sv"): void {
    this.lang = lang;
  }

  /**
   * Gets the current user interface language
   * @returns One of the supported languages: "fi", "en" or "sv".
   */
  public getLanguage(): "fi" | "en" | "sv" {
    return this.lang as "fi" | "en" | "sv";
  }

  /** Gets the current core dictionary (all languages). */
  public getCurrentDictionary(): TranslationsDictionary {
    return this.dictionary;
  }

  /**
   * Loads all languages from server using the locales path.
   * @returns The full dictionary (this is not stored to current dictionary - just fetched).
   */
  public async getBaseDictionary(): Promise<TranslationsDictionary> {
    const promises: any[] = [];
    ["fi", "sv", "en"].forEach(lang => {
      promises.push(this.fetchLanguage(lang));
    });
    const values = await Promise.all(promises);
    const obj: any = {};
    obj["fi"] = values[0];
    obj["sv"] = values[1];
    obj["en"] = values[2];
    return obj;
  }

  /**
   * Fetches a single language from server language This method fails outside browser.
   * @param lang url param
   * @returns One language as dictionary object (this is not stored to current dictionary - just fetched).
   */
  private async fetchLanguage(lang: string): Promise<DictionaryLang> {

    if (this.localesFunc) {
      return this.localesFunc(lang);
    }

    const url = `${this.localesPath}/${lang}.json?t=${new Date().getTime()}`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      // Do checks if necessary.
      return data;
    }
    throw new Error(`Unable to fetch language "${url}"`);
  }

  /**
   * Merges objects.
   * @param objects - Objects to merge.
   */
  private mergeDeep(...objects: any[]) {
    const isObject = (obj: any) => obj && typeof obj === "object";

    return objects.reduce((result, current) => {
      Object.keys(current).forEach((key) => {
        const currentVal = current[key];
        const resultVal = result[key];

        if (Array.isArray(resultVal) && Array.isArray(currentVal)) {
          result[key] = resultVal.concat(...currentVal);
        } else if (isObject(resultVal) && isObject(currentVal)) {
          result[key] = this.mergeDeep(resultVal, currentVal);
        } else {
          result[key] = currentVal;
        }
      });
      return result;
    }, {});
  }

  private readValue(object: any, path: string, defaultValue: any): any {
    if (!object || !path) {
      return defaultValue;
    }
    const subKeys = path.split(".");
    let value = object;
    for (const subKey of subKeys) {
      if (Objects.has(value, subKey)) {
        value = value[subKey];
      } else {
        return defaultValue;
      }
    }
    return value;
  }

  private isBrowser(): boolean {
    return typeof globalThis.fetch !== "undefined";
  }

}
